import { action, get } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Changeset } from 'ember-changeset';
import metricValidations from 'scaleflow/validators/metric';
import targetValidations from 'scaleflow/validators/target';
import decimalHint from 'scaleflow/utils/decimal-hint';

export default class InitiativesMetricWizardComponent extends Component {
  @tracked activeStep = 0;
  @tracked metricChangeset = {};
  @tracked targetChangeset = {};
  @tracked goalSearch = '';
  @tracked metricsSearch = '';
  @tracked isNewMetric = false;
  @tracked targetChangesets = [];

  @tracked selectedMetrics = [];
  @tracked isSaving = false;

  @tracked metric = null;
  @tracked target = null;
  @tracked isAdvancedOpen = false;

  MetricValidations = metricValidations;
  TargetValidations = targetValidations;

  @service betaToggle;
  @service store;
  @service router;

  @action
  setActiveStep(index) {
    this.activeStep = index;
  }

  @action
  toggleAdvanced() {
    this.isAdvancedOpen = !this.isAdvancedOpen;
  }

  @action
  decimalHint(val) {
    return decimalHint(val, Number(this.metricChangeset.decimals));
  }

  get isDisabled() {
    if (this.isNewMetric) {
      return this.metricChangeset.isInvalid || this.metricChangeset.isPristine;
    } else {
      return !this.selectedMetrics.length;
    }
  }

  metricValidator({ key, newValue }) {
    let validator = get(metricValidations, key);
    if (typeof validator === 'function') {
      return validator(key, newValue);
    }
  }

  targetValidator({ key, newValue }) {
    let validator = get(targetValidations, key);
    if (typeof validator === 'function') {
      return validator(key, newValue);
    }
  }

  @action
  onDescriptionChange(changeset, e) {
    changeset.set('description', e);
  }

  @action
  async saveMetrics() {
    this.isSaving = true;
    try {
      if (this.isNewMetric) {
        this.metricChangeset.changes.forEach((change) => {
          this.metric.set(change.key, change.value);
        });
        await this.metric.save();
        const target = this.store.createRecord('target', {
          target_value: this.targetChangeset.target_value,
          end_date: this.targetChangeset.end_date,
          initiative: this.args.initiative,
          metric: this.metric,
        });
        await target.save();
      } else {
        for (let i = 0; i < this.selectedMetrics.length; i++) {
          const target = this.store.createRecord('target', {
            target_value: this.targetChangesets[i].target_value,
            end_date: this.targetChangesets[i].end_date,
            initiative: this.args.initiative,
            metric: this.selectedMetrics[i],
          });
          await target.save();
        }
      }
      this.args?.closeWizard();
    } finally {
      this.isSaving = false;
    }
  }

  @action
  rollbackChanges(changeset) {
    if (!changeset) {
      this.metricChangeset?.rollback?.();
      this.targetChangeset?.rollback?.();
      this.clearSelectedMetrics();
    } else changeset.rollback();
  }

  @action
  goToListPage() {
    this.router.transitionTo('business.metrics');
  }

  get metrics() {
    try {
      const initiativeMetrics = this.args.initiative.metrics.reduce(
        (acc, item) => {
          if (item.content?.id) acc[item.content.id] = true;
          return acc;
        },
        {},
      );
      return this.store
        .peekAll('metric')
        .filter((metric) => !initiativeMetrics[metric.id]);
    } catch (e) {
      console.log(e);
      return [];
    }
  }

  @action
  targetChangesetFactory() {
    const target = this.store.createRecord('target', {});
    return Changeset(target, this.targetValidator);
  }

  @action
  clearSelectedMetrics() {
    for (let i = 0; i < this.selectedMetrics.length; i++) {
      this.selectedMetrics[i].deleteRecord();
      this.targetChangesets[i].rollback();
    }
    this.selectedMetrics = [];
    this.targetChangesets = [];
  }

  @action
  handleMetricSelect(metric, e) {
    const checked = e.target.checked;
    if (checked === undefined) return;
    const metrics = this.selectedMetrics;
    const targetChangesets = this.targetChangesets;
    if (checked) {
      metrics.push(metric);
      targetChangesets.push(this.targetChangesetFactory());
    } else {
      const index = metrics.findIndex((_metric) => _metric.id === metric.id);
      if (index < 0) return;
      else {
        metrics.splice(index, 1);
        targetChangesets.splice(index, 1);
      }
    }
    this.selectedMetrics = metrics;
    this.targetChangesets = targetChangesets;
  }

  @action
  showMetricForm() {
    this.isNewMetric = true;
    this.metric = this.store.createRecord('metric');
    this.metricChangeset = Changeset(this.metric, this.metricValidator);
    this.target = this.store.createRecord('target');
    this.targetChangeset = Changeset(this.target, this.targetValidator);
  }

  @action
  hideMetricForm() {
    this.isNewMetric = false;
    this.metricChangeset = null;
    this.metric?.deleteRecord();
    this.metric = null;
    this.targetChangeset = null;
    this.target?.deleteRecord();
    this.target = null;
  }

  @action
  onFormCancel() {
    this.rollbackChanges();
    this.metric?.deleteRecord?.();
    this.metric = null;
    this.target?.deleteRecord?.();
    this.target = null;
    this.activeStep = 0;
    this.isNewMetric = false;
    this.args?.closeWizard();
  }
}
